<script setup>
import {useAuthenticator} from "@aws-amplify/ui-vue";
import "@aws-amplify/ui-vue/styles.css";

import {signInWithRedirect} from 'aws-amplify/auth';
import {Hub} from 'aws-amplify/utils';

import LayoutComponent from "@/UI/LayoutComponent.vue";

import {userStore} from "@/Stores/UserStore";
import {onMounted, onUnmounted, reactive, watch} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";

const data = reactive({
  loadingOverlay: false
});

const auth = useAuthenticator();

// Called when user signs out
const clearUserInfo = () => {
  userStore.setIdToken(null);
  userStore.setUserInfo(null);
}

// Called on initial page load if session is valid or on signin/token refresh
const getUserInfo = async () => {
  const tokens = await ApiWrapper.getSession();

  return tokens?.idToken;
}

let hubListenerCancelToken;
onMounted(async () => {
  // Listen for amplify auth events
  hubListenerCancelToken = Hub.listen('auth', async ({payload}) => {
    switch (payload.event) {
      case 'signedIn':
      case 'signInWithRedirect':
        await getUserInfo();
        break;
      case 'signedOut':
        clearUserInfo();
        break;
    }
  });

  const session = await getUserInfo();
  // If user is not logged in, then redirect to hosted UI
  if (!session) {
    await signInWithRedirect();
  }
});

watch(() => userStore.refreshingToken, async (val) => {
  data.loadingOverlay = userStore.refreshingToken;
});

onUnmounted(() => {
  hubListenerCancelToken();
})

</script>

<template>
  <template v-if="!userStore.userInfo">
    <v-skeleton-loader
        class="mx-auto mt-7"
        elevation="2"
        max-width="700"
        type="card, chip, actions"
    />
  </template>

  <template v-if="userStore.userInfo">
    <LayoutComponent>
      <template #sidebar-top>
        <div class="ml-2 mr-2 mt-2 mb-2">
          <h3 style="text-align: center">ClaimsLink</h3>
        </div>
        <v-divider></v-divider>
        <div class="ml-2 mr-2 mt-2">
          <b>{{ userStore.userInfo.name }}</b>
        </div>
        <div class="ml-2 mr-2" style="font-size:14px">
          {{ userStore.userInfo.username }}
        </div>
      </template>

      <template #sidebar-shortcuts>
        <div style="background-color: #091736; color: white;">
          <v-list-item v-if="!Permissions.isAdUser()" prepend-icon="mdi-form-textbox-password" to="/dashboards/change-password">
            <v-list-item-title class="sidebar-shortcut">
              Change Password
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.account-manager'])" prepend-icon="mdi-account-tie" to="/dashboards/account-manager">
            <v-list-item-title class="sidebar-shortcut">
              Account Manager Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.adjuster'])" prepend-icon="mdi-account-tie" to="/dashboards/adjuster">
            <v-list-item-title class="sidebar-shortcut">
              Adjuster Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.admin'])" prepend-icon="mdi-account-tie" to="/dashboards/admin">
            <v-list-item-title class="sidebar-shortcut">
              Admin Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.aging-receivables'])" prepend-icon="mdi-file-chart" to="/dashboards/aging-receivables">
            <v-list-item-title class="sidebar-shortcut">
              Aging Receivables Report
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.batches'])" prepend-icon="mdi-bank-transfer" to="/dashboards/batches">
            <v-list-item-title class="sidebar-shortcut">
              Batch Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.bulk-assign-claims'])" prepend-icon="mdi-earth" to="/dashboards/bulk-assign-claims">
            <v-list-item-title class="sidebar-shortcut">
              Bulk Assign Claims
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.charts'])" prepend-icon="mdi-finance" to="/dashboards/charts">
            <v-list-item-title class="sidebar-shortcut">
              Charts Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.claims'])" prepend-icon="mdi-clipboard-text" to="/dashboards/claims">
            <v-list-item-title class="sidebar-shortcut">
              Claims Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.clients'])" prepend-icon="mdi-account-tie" to="/dashboards/clients">
            <v-list-item-title class="sidebar-shortcut">
              Client Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.client-portal'])" prepend-icon="mdi-account-tie-outline" to="/dashboards/client-portal">
            <v-list-item-title class="sidebar-shortcut">
              Client Portal
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['invoices.read-collection'])" prepend-icon="mdi-file-chart" to="/collectionsreport">
            <v-list-item-title class="sidebar-shortcut">
              Collections Report
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.financial'])" prepend-icon="mdi-bank-outline" to="/claimsco/financials">
            <v-list-item-title class="sidebar-shortcut">
              Financial Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.marketing'])" prepend-icon="mdi-account-tie" to="/dashboards/marketing">
            <v-list-item-title class="sidebar-shortcut">
              Marketing Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.messaging'])" prepend-icon="mdi-message-reply-text" to="/dashboards/messaging">
            <v-list-item-title class="sidebar-shortcut">
              Messaging Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['marketing.list'])" prepend-icon="mdi-currency-usd" to="/mymarketingpay">
            <v-list-item-title class="sidebar-shortcut">
              My Marketing Pay
            </v-list-item-title>
          </v-list-item>

          <v-list-item prepend-icon="mdi-account-circle" to="/dashboards/my-profile">
            <v-list-item-title class="sidebar-shortcut">
              My Profile
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.pay-details'])" prepend-icon="mdi-cash-multiple" to="/dashboards/pay-details">
            <v-list-item-title class="sidebar-shortcut">
              Pay Details Dash
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.payments'])" prepend-icon="mdi-bank-transfer-out" to="/dashboards/payments">
            <v-list-item-title class="sidebar-shortcut">
              Payments Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.reports'])" prepend-icon="mdi-file-chart" to="/dashboards/reports">
            <v-list-item-title class="sidebar-shortcut">
              Reports Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['reports.revenue'])" prepend-icon="mdi-file-chart" to="/reportclientrevenue">
            <v-list-item-title class="sidebar-shortcut">
              Client Revenue Report
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['reports.claims'])" prepend-icon="mdi-file-chart" to="/reportclientclaims">
            <v-list-item-title class="sidebar-shortcut">
              Monthly Volume Report
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.returned-for-corrections'])" prepend-icon="mdi-clipboard-clock" to="/dashboards/returnedforcorrections">
            <v-list-item-title class="sidebar-shortcut">
              Corrections Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.reviewer'])" prepend-icon="mdi-account-tie" to="/dashboards/reviewer">
            <v-list-item-title class="sidebar-shortcut">
              Reviewer Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['claims.search'])" prepend-icon="mdi-magnify" to="/dashboards/search-claims">
            <v-list-item-title class="sidebar-shortcut">
              Search Claims
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['users.search'])" prepend-icon="mdi-magnify" to="/dashboards/search-users">
            <v-list-item-title class="sidebar-shortcut">
              Search Users
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['claims.search'])" prepend-icon="mdi-magnify" to="/invoicesearch">
            <v-list-item-title class="sidebar-shortcut">
              Search Invoices
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['claims.create'])" prepend-icon="mdi-email-edit-outline" to="/emailimportclaims">
            <v-list-item-title class="sidebar-shortcut">
              Email Import Claims
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions(['dashboards.users'])" prepend-icon="mdi-account-circle-outline" to="/dashboards/users">
            <v-list-item-title class="sidebar-shortcut">
              User Dashboard
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="Permissions.checkPermissions([], true)" prepend-icon="mdi-file-tree" to="/dashboards/manage-roles">
            <v-list-item-title class="sidebar-shortcut">
              Manage Roles
            </v-list-item-title>
          </v-list-item>
        </div>
      </template>

      <template #sidebar-bottom>
        <div class="pa-2">
          <v-btn block color="primary" @click="auth.signOut">
            Logout
          </v-btn>
        </div>
      </template>

      <template #page-content>
        <v-overlay v-model="data.loadingOverlay" :opacity=".35" persistent style="width: 100vw!important; height: 100vh!important;" fluid>
          <v-row justify="center" align="center" style="height:100vh!important;width:100vw!important;">
            <v-progress-circular
                indeterminate
                size="64"
            ></v-progress-circular>
          </v-row>
        </v-overlay>
        <RouterView/>
      </template>
    </LayoutComponent>
  </template>
</template>

<style>
body {
  background-color: #F5F5F5;
}
</style>
